/**
 * Branch / Multi-location – Firestore path (Rule 1):
 * Collection: artifacts/{appId}/users/{userId}/locations
 *
 * Schema:
 * - name, address, managerPhone, isActive, latitude, longitude
 * - businessType: 'cafe' | 'restaurant' | 'bakery' | 'other'
 * - customBusinessTypeLabel?: string (khi businessType === 'other')
 * - settings: { themeColor?, themePrimary?, logoUrl?, defaultView?, brandColor? } (brandColor = màu nút POS)
 *   (themeColor/themePrimary: màu chủ đạo POS; defaultView: 'menu' | 'table' cho Nhà hàng)
 * - useKds: boolean – Sử dụng Hệ thống Bếp (KDS). Khi false, đơn hàng thanh toán/gửi đi tự động completed.
 * - kitchenStations: Array<{ id, name }> – Trạm bếp (Bar, Bếp Nóng...) cho KDS và gán món.
 * - paymentConfig: { bankBin?, accountNumber?, accountName?, template? } – VietQR (BIN, STK, tên chủ TK, template).
 * Menu items (brand_kit) filter by locationIds array – POS đọc theo selectedLocationId.
 */

import { posDomain } from '@domain/pos/PosDomainFactory';
import { createAuditLog } from './createAuditLog';
import { POS_CONFIG_APP_ID } from '@config/constants';
export { POS_CONFIG_APP_ID };

/**
 * Listen to all locations (for Merchant Center and Branch Switcher).
 * @param {string} appId - Legacy param, preserved for signature compatibility
 * @param {string} userId - Owner ID
 * @param {(locations: any[]) => void} callback
 * @returns {() => void} Unsubscribe
 */
export function listenLocations(appId, userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  const repo = posDomain.getBranchRepository();
  return repo.listenLocations(userId, callback);
}

/**
 * Add a new location.
 * @param {string} appId
 * @param {string} userId
 * @param {Object} data
 * @returns {Promise<string>} Document ID
 */
export async function addLocation(appId, userId, data) {
  if (!userId) throw new Error('userId is required');
  
  const rawSettings = data.settings && typeof data.settings === 'object' ? data.settings : {};
  const settings = {
    themeColor: rawSettings.themeColor ?? rawSettings.themePrimary ?? rawSettings.brandColor ?? '',
    themePrimary: rawSettings.themePrimary ?? rawSettings.themeColor ?? rawSettings.brandColor ?? '',
    brandColor: rawSettings.brandColor ?? rawSettings.themeColor ?? rawSettings.themePrimary ?? '',
    logoUrl: rawSettings.logoUrl ?? '',
    menuFileUrl: rawSettings.menuFileUrl ?? '',
    defaultView: ['menu', 'table'].includes(rawSettings.defaultView) ? rawSettings.defaultView : 'menu',
  };

  const payload = {
    name: (data.name || '').trim(),
    address: (data.address || '').trim() || null,
    manager_phone: (data.managerPhone || '').trim() || null,
    is_active: data.isActive !== false,
    latitude: data.latitude != null ? Number(data.latitude) : null,
    longitude: data.longitude != null ? Number(data.longitude) : null,
    business_type: ['cafe', 'restaurant', 'bakery', 'other'].includes(data.businessType) ? data.businessType : (data.businessType && String(data.businessType).trim() ? 'other' : 'cafe'),
    settings,
    use_kds: data.useKds === true,
    kitchen_stations: Array.isArray(data.kitchenStations) ? data.kitchenStations : [],
    payment_config: data.paymentConfig && typeof data.paymentConfig === 'object' ? {
      bankBin: String(data.paymentConfig.bankBin || '').trim() || null,
      accountNumber: String(data.paymentConfig.accountNumber || '').trim() || null,
      accountName: String(data.paymentConfig.accountName || '').trim() || null,
      template: ['compact', 'compact2', 'qr_only', 'print'].includes(data.paymentConfig.template) ? data.paymentConfig.template : 'compact',
    } : null,
    allowed_ips: Array.isArray(data.allowedIps) ? data.allowedIps : [],
  };

  const repo = posDomain.getBranchRepository();
  const locationId = await repo.addLocation(userId, payload);

  createAuditLog('BRANCH_CREATE', 'INFO', `Tạo chi nhánh mới: ${payload.name}`, {
    locationId,
    appId,
    branchName: payload.name
  });

  return locationId;
}

/**
 * Update a location.
 */
export async function updateLocation(appId, userId, locationId, data) {
  if (!userId || !locationId) throw new Error('userId and locationId required');
  
  const payload = {};
  if (data.name !== undefined) payload.name = String(data.name).trim();
  if (data.address !== undefined) payload.address = (data.address || '').trim() || null;
  if (data.managerPhone !== undefined) payload.manager_phone = (data.managerPhone || '').trim() || null;
  if (data.isActive !== undefined) payload.is_active = !!data.isActive;
  if (data.latitude !== undefined) payload.latitude = data.latitude != null ? Number(data.latitude) : null;
  if (data.longitude !== undefined) payload.longitude = data.longitude != null ? Number(data.longitude) : null;
  if (data.businessType !== undefined) payload.business_type = data.businessType;
  if (data.settings !== undefined) payload.settings = data.settings;
  if (data.useKds !== undefined) payload.use_kds = !!data.useKds;
  if (data.kitchenStations !== undefined) payload.kitchen_stations = data.kitchenStations;
  if (data.paymentConfig !== undefined) payload.payment_config = data.paymentConfig;
  if (data.allowedIps !== undefined) payload.allowed_ips = data.allowedIps;

  const repo = posDomain.getBranchRepository();
  await repo.updateLocation(userId, locationId, payload);

  createAuditLog('BRANCH_UPDATE', 'INFO', `Cập nhật chi nhánh: ${data.name || locationId}`, {
    locationId,
    appId
  });
}

/**
 * Delete a location.
 */
export async function deleteLocation(appId, userId, locationId) {
  const repo = posDomain.getBranchRepository();
  await repo.deleteLocation(userId, locationId);

  createAuditLog('BRANCH_DELETE', 'WARNING', `Xóa chi nhánh ID: ${locationId}`, {
    locationId,
    appId
  });
}

export function getActiveLocations(locations) {
  return (locations || []).filter((l) => l.is_active !== false);
}


